import { useState, useEffect, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const CONSENT_KEY = "hoffmann_cookie_consent";

const useCookieConsent = () => {
  const [consent, setConsent] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem(CONSENT_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setConsent(parsed);
        setShowBanner(false);
      } catch (e) {
        setShowBanner(true);
      }
    } else {
      // Small delay so the page loads first
      const timer = setTimeout(() => setShowBanner(true), 800);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveConsent = useCallback(async (preferences) => {
    const data = {
      necessary: true,
      statistics: preferences.statistics || false,
      marketing: preferences.marketing || false,
    };

    try {
      const res = await axios.post(`${API}/cookie-consent`, data);
      const consentRecord = {
        ...data,
        consent_id: res.data.consent_id,
        timestamp: res.data.created_at || new Date().toISOString(),
      };
      localStorage.setItem(CONSENT_KEY, JSON.stringify(consentRecord));
      setConsent(consentRecord);
    } catch {
      // Fallback: save locally even if backend fails
      const consentRecord = { ...data, timestamp: new Date().toISOString() };
      localStorage.setItem(CONSENT_KEY, JSON.stringify(consentRecord));
      setConsent(consentRecord);
    }

    setShowBanner(false);
    setShowSettings(false);
  }, []);

  const acceptAll = useCallback(() => {
    saveConsent({ necessary: true, statistics: true, marketing: true });
  }, [saveConsent]);

  const rejectAll = useCallback(() => {
    saveConsent({ necessary: true, statistics: false, marketing: false });
  }, [saveConsent]);

  const openSettings = useCallback(() => {
    setShowSettings(true);
  }, []);

  const revokeConsent = useCallback(() => {
    localStorage.removeItem(CONSENT_KEY);
    setConsent(null);
    setShowBanner(true);
    setShowSettings(false);
  }, []);

  return {
    consent,
    showBanner,
    showSettings,
    setShowSettings,
    acceptAll,
    rejectAll,
    saveConsent,
    openSettings,
    revokeConsent,
  };
};

export default useCookieConsent;
